import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = "google-maps-places-script";

// Global auth-failure flag. Google Maps calls window.gm_authFailure() when the API key is
// missing / not authorised / billing disabled — WITHOUT this hook it renders a full-page
// dark modal that blocks the whole form. We intercept it, silence the modal, and mark the
// script permanently unavailable so every AddressAutocomplete falls back to plain typing.
if (typeof window !== "undefined") {
  window.__delco_maps_failed = window.__delco_maps_failed || false;
  window.gm_authFailure = () => {
    window.__delco_maps_failed = true;
    // Remove the injected error dialog that Google's script inserts into <body>.
    document.querySelectorAll("div.gm-err-container, .gm-err-autocomplete").forEach(n => n.remove());
  };

  // Google's `.pac-container` is appended to <body> — OUTSIDE any Radix Dialog. Radix
  // listens for `pointerdown` on document to detect "click-outside" and close the dialog.
  // If we don't stop that, clicking a suggestion inside a Dialog closes the Dialog before
  // Google's selection registers. Google Autocomplete internally uses `mousedown` on its
  // items to keep input focus, so we do NOT interfere with mousedown at all.
  if (!window.__delco_pac_hooked) {
    window.__delco_pac_hooked = true;
    const inPac = (t) => t && t.closest && t.closest(".pac-container");
    document.addEventListener("pointerdown", (e) => {
      if (inPac(e.target)) e.stopPropagation();
    }, true);
    document.addEventListener("touchstart", (e) => {
      if (inPac(e.target)) e.stopPropagation();
    }, true);
  }
}

let scriptPromise = null;

function pollForAutocomplete(timeoutMs = 8000) {
  // Once the loader is done, `google.maps.places.Autocomplete` may still take
  // a moment to appear (importLibrary('places') is async). Poll for it.
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (window.google?.maps?.places?.Autocomplete) return resolve(window.google);
      if (window.__delco_maps_failed) return resolve(null);
      if (Date.now() - start > timeoutMs) return resolve(null);
      setTimeout(tick, 200);
    };
    tick();
  });
}

function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.__delco_maps_failed) return Promise.resolve(null);
  if (window.google?.maps?.places?.Autocomplete) return Promise.resolve(window.google);
  if (scriptPromise) return scriptPromise;
  if (!MAPS_KEY) return Promise.resolve(null);

  scriptPromise = new Promise((resolve) => {
    const finish = async () => {
      try {
        if (window.google?.maps?.importLibrary) {
          await window.google.maps.importLibrary("places");
        }
      } catch (e) {
        console.warn("[AddressAutocomplete] importLibrary('places') failed:", e?.message || e);
      }
      // importLibrary sometimes resolves before window.google.maps.places.Autocomplete
      // is fully attached — poll one more layer just in case.
      const g = await pollForAutocomplete();
      resolve(g);
    };
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      // Whether the script already loaded or not, defer to `finish` after tick.
      if (window.google?.maps) finish();
      else existing.addEventListener("load", finish, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places&v=weekly&loading=async`;
    s.onload = finish;
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Google Places–powered address picker. Always single-line (Google's UX standard).
 * Falls back to a plain <input> when the Maps script errors or `gm_authFailure` fires
 * — no popup, no console spam, users can still type addresses.
 */
export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Building, sector, landmark",
  required = false,
  "data-testid": testId,
  className = "",
  // eslint-disable-next-line no-unused-vars
  multiline,
}) {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((g) => {
      if (cancelled) return;
      if (!g || !inputRef.current) return;
      if (!(inputRef.current instanceof HTMLInputElement)) return;
      if (window.__delco_maps_failed) return;
      try {
        const ac = new g.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: ["in"] },
          fields: ["formatted_address", "geometry", "address_components", "name"],
          types: ["geocode"],
        });
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          const address = place.formatted_address || place.name || "";
          const lat = place.geometry?.location?.lat?.();
          const lng = place.geometry?.location?.lng?.();
          if (address && onChange) onChange(address);
          if (onSelect) onSelect({ address, lat, lng, components: place.address_components });
        });
        acRef.current = ac;
        setReady(true);
      } catch (err) {
        console.warn("[AddressAutocomplete] Places Autocomplete failed to attach:", err?.message || err);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        autoComplete="off"
        className={`${className} ${ready ? "pr-9" : ""}`}
      />
      {ready && (
        <MapPin
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60 pointer-events-none"
          aria-hidden
        />
      )}
    </div>
  );
}
